import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import cpoTransactionApi from '../../utils/api/cpo-transaction';
import TabToggle from '../tab-toggle/TabToggle';

interface CpoCdrSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * Formats total time in hours into a short duration string (e.g. 1m 22s).
 * @param {number | string | undefined | null} totalTime - Total time in hours.
 * @returns {string} Formatted duration string.
 */
const formatDuration = (totalTime: number | string | undefined | null): string => {
  if (totalTime == null || Number.isNaN(Number(totalTime))) {
    return '—';
  }

  const totalSeconds = Math.round(Number(totalTime) * 3600);
  if (totalSeconds <= 0) {
    return '—';
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  return parts.join(' ') || '—';
};

/**
 * Formats cost amount with currency symbol or code.
 * @param {number | string | undefined | null} amount - Cost amount.
 * @param {string} [currency] - Currency string.
 * @returns {string} Formatted currency text.
 */
const formatCurrency = (
  amount: number | string | undefined | null,
  currency?: string,
): string => {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '—';
  }
  const numericValue = Number(amount);
  const formattedValue = ToFixedValue(numericValue, 2);
  if (currency === 'INR' || !currency) {
    return `₹${formattedValue}`;
  }
  return `${formattedValue} ${currency}`;
};

/**
 * Formats date to DD/MM/YYYY, HH:MM AM/PM string format.
 * @param {string | undefined | null} dateString - Date string in UTC.
 * @returns {string} Formatted date time string.
 */
const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '—';
  try {
    const dateValue = ConvertUTCtoDate(dateString);
    const timeValue = FormatUtcString(ConvertUTCtoHHMM(dateString));
    return `${dateValue}, ${timeValue}`;
  } catch {
    return String(dateString);
  }
};

/**
 * CpoCdrSidePanel component for displaying Charge Detail Records with a 2-tab drawer layout.
 * @param {CpoCdrSidePanelProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered side panel element.
 */
function CpoCdrSidePanel(properties: CpoCdrSidePanelProperties): React.JSX.Element {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [cdrData, setCdrData] = useState<any>();
  const [activeTab, setActiveTab] = useState<'details' | 'transaction'>('details');

  /**
   * Fetch CPO CDR details by ID
   * @param {string} transactionId - Transaction ID
   */
  const handleFetchCpoTransactionDetails = (transactionId: string) => {
    cpoTransactionApi
      .fetchCpoTransactionById(transactionId)
      .then((response) => {
        setCdrData(response?.data?.data || response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message:
              error?.response?.data?.message ||
              t('errorMessage', { defaultValue: 'Failed to fetch transaction details.' }),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  useEffect(() => {
    let transactionObjectId: string | null = null;
    if (fetchIdFrom === 'queryParams') {
      transactionObjectId =
        searchParameters.get('transactionObjectId') ||
        searchParameters.get('cdrObjectId') ||
        searchParameters.get('cdrId') ||
        searchParameters.get('id') ||
        searchParameters.get('sessionId');
    } else if (fetchIdFrom === 'props') {
      transactionObjectId = id || null;
    }

    if (!transactionObjectId) {
      return;
    }

    handleFetchCpoTransactionDetails(transactionObjectId);
  }, [searchParameters, id]);

  const totalCostValue =
    cdrData?.totalCost?.inclVat ??
    cdrData?.totalCost?.exclVat ??
    cdrData?.totalCost ??
    cdrData?.amount;
  const energyValue = cdrData?.totalEnergy ?? cdrData?.powerConsumedInKwh ?? 0;
  const currencyCode = cdrData?.currency || 'INR';

  return (
    <div className="cpo-cdr-side-panel-wrap">
      <div className="panel-scrollable-content">
        {/* Teal Header Summary Card */}
        <div className="cpo-summary-card">
          <div className="final-amount-block">
            <div className="amount-icon-circle">
              <svg viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </div>
            <div className="amount-text-group">
              <span className="amount-title">
                {t('finalAmount', { defaultValue: 'Final Amount' })}
              </span>
              <span className="amount-value">{formatCurrency(totalCostValue, currencyCode)}</span>
            </div>
          </div>
          <div className="summary-metrics-group">
            <div className="metric-col">
              <span className="metric-label">{t('energy', { defaultValue: 'Energy' })}</span>
              <span className="metric-val">{`${ToFixedValue(energyValue, 3)} kWh`}</span>
            </div>
            <div className="metric-col">
              <span className="metric-label">{t('duration', { defaultValue: 'Duration' })}</span>
              <span className="metric-val">{formatDuration(cdrData?.totalTime)}</span>
            </div>
            <div className="metric-col">
              <span className="metric-label">{t('totalCost', { defaultValue: 'Total Cost' })}</span>
              <span className="metric-val">{formatCurrency(totalCostValue, currencyCode)}</span>
            </div>
          </div>
        </div>

        {/* 2 Top Tab Toggle Navigation */}
        <TabToggle
          tabs={[
            {
              label: t('detailsTransaction', { defaultValue: 'Details' }),
              value: 'details',
            },
            {
              label: t('chargingDetails', { defaultValue: 'Charging Details' }),
              value: 'transaction',
            },
          ]}
          defaultTab="details"
          isLabelValue
          onChange={(value) => setActiveTab(value as 'details' | 'transaction')}
        />

        {/* Tab 1: Details Transaction */}
        {activeTab === 'details' && (
          <>
            {/* CDR Information Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge cdr-information">
                  <svg viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <h3>{t('cdrInformation', { defaultValue: 'CDR Information' })}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('sessionId', { defaultValue: 'Session ID' })}
                    </span>
                    <span className="item-val">{cdrData?.sessionId || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('authentication', { defaultValue: 'Authentication' })}
                    </span>
                    <span className="item-val">{cdrData?.authMethod || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('transactionId', { defaultValue: 'Transaction ID' })}
                    </span>
                    <span className="item-val">{cdrData?.transactionId || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('startTime', { defaultValue: 'Start Time' })}</span>
                    <span className="item-val">{formatDateTime(cdrData?.startDateTime)}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('chargingPoint', { defaultValue: 'Charging Point' })}
                    </span>
                    <span className="item-val">{cdrData?.chargingPointSn || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('endTime', { defaultValue: 'End Time' })}</span>
                    <span className="item-val">{formatDateTime(cdrData?.endDateTime)}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('accountId', { defaultValue: 'Account ID' })}</span>
                    <span className="item-val">
                      {cdrData?.accountCustomId || cdrData?.userCustomId || '—'}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('currency', { defaultValue: 'Currency' })}</span>
                    <span className="item-val">{cdrData?.currency || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge location">
                  <svg viewBox="0 0 24 24">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <h3>{t('location', { defaultValue: 'Location' })}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('locationId', { defaultValue: 'Location ID' })}
                    </span>
                    <span className="item-val">{cdrData?.cdrLocation?.id || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('evseUid', { defaultValue: 'EVSE UID' })}</span>
                    <span className="item-val">{cdrData?.cdrLocation?.evseUid || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('connectorId', { defaultValue: 'Connector ID' })}
                    </span>
                    <span className="item-val">{cdrData?.cdrLocation?.connectorId ?? '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Tab 2: Transaction Details */}
        {activeTab === 'transaction' && (
          <>
            {/* Energy & Cost Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge energy-cost">
                  <svg viewBox="0 0 24 24">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                </div>
                <h3>{t('energyCost', { defaultValue: 'Energy & Cost' })}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('energyUsed', { defaultValue: 'Energy Used' })}
                    </span>
                    <span className="item-val">{`${ToFixedValue(energyValue, 3)} kWh`}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('duration', { defaultValue: 'Duration' })}</span>
                    <span className="item-val">{formatDuration(cdrData?.totalTime)}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('meterStart', { defaultValue: 'Meter Start' })}
                    </span>
                    <span className="item-val">{ToFixedValue(cdrData?.meterStart,3 )?? '0.000'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('meterStop', { defaultValue: 'Meter Stop' })}</span>
                    <span className="item-val">
                      {cdrData?.meterStop != null ? `${ToFixedValue(cdrData.meterStop,3)}` : '0.000'}
                    </span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">{t('totalCost', { defaultValue: 'Total Cost' })}</span>
                    <span className="item-val">{formatCurrency(totalCostValue, currencyCode)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tariff Information Card */}
            <div className="cpo-section-card">
              <div className="cpo-card-header">
                <div className="header-icon-badge tariff">
                  <svg viewBox="0 0 24 24">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <h3>{t('tariffInformation', { defaultValue: 'Tariff Information' })}</h3>
              </div>
              <div className="cpo-card-grid">
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('tariffName', { defaultValue: 'Tariff Name' })}
                    </span>
                    <span className="item-val">{cdrData?.tariffs?.[0]?.name || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('currentType', { defaultValue: 'Current Type' })}
                    </span>
                    <span className="item-val">{cdrData?.tariffs?.[0]?.currentType || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('tariffType', { defaultValue: 'Tariff Type' })}
                    </span>
                    <span className="item-val">{cdrData?.tariffs?.[0]?.type || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('chargingPointSn', { defaultValue: 'Charging Point SN' })}
                    </span>
                    <span className="item-val">{cdrData?.chargingPointSn || '—'}</span>
                  </div>
                </div>
                <div className="cpo-item-cell">
                  <div className="item-content">
                    <span className="item-lbl">
                      {t('transactionEndReason', { defaultValue: 'Transaction End Reason' })}
                    </span>
                    <span className="item-val">{cdrData?.transactionEndReason || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Bottom Disclaimer Banner */}
        <div className="cpo-info-banner">
          <div className="info-banner-icon">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>
          <span>
            {t('disclaimerText', {
              defaultValue: 'All values are as per the Charge Detail Record.',
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

export default CpoCdrSidePanel;
